import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RouteComponentProps, useLocation } from 'react-router-dom';
import moment from 'moment-timezone';
import 'moment/locale/pt-br';

import AuthorizationContainer from '../../components/AuthorizationContainer';
import Button from '../../components/Button';
import EstateUnitFilter from '../../components/EstateUnitFilter';
import FilterBar, { FilterField, FilterValues } from '../../components/Filter';
import { ReportModal } from '../../components/ReportModal';
import { CustoDropDownItemProps } from '../../components/types';
import { GetCanceledBondInsurancesResponseDTO } from '../../dtos/canceled-bond-insurance/get-canceled-bond-insurances-response.dto';
import { ActionEnum } from '../../enums/authz-action.enum';
import { FeatureEnum } from '../../enums/authz-feature.enum';
import { CanceledStatusEnum } from '../../enums/status-canceled-bond-insurances';
import { getUsers } from '../../services/broker';
import {
  getCanceledBondInsurances,
  getCanceledBondInsurancesReport
} from '../../services/canceled-bond-insurance';
import { dangerNotification } from '../../services/notification';
import { iRootState } from '../../store';
import { AvailableBrokersAndEstatesState } from '../../store/types/temp-types';
import { UsersToCustomDropDownOptions } from '../../util';
import { appendEstateUnitIdToFilter } from '../../utils/estateUnitFilter.util';
import { isBackOfficeUser } from '../../utils/isBackOfficeUser';
import { isEstateRole } from '../../utils/isEstateRole';
import { UserForUpdateEstateForm } from '../estates-list/types';
import CreateCancelationModal from './components/create-cancelation-modal/Modal';
import CanceledBondInsurancesTable from './CanceledBondInsurancesTable';
import {
  CanceledOrigin,
  FilterData,
  ListingRouteInfo,
  searchTypeOptions
} from './types';

export type BondInsuranceListProps = {
  availableBrokersAndEstates: iRootState['availableBrokersAndEstates'];
  user: iRootState['user'];
  updateAvailableBrokersAndEstates: (
    availableBrokers: AvailableBrokersAndEstatesState
  ) => AvailableBrokersAndEstatesState | void;
} & RouteComponentProps<ListingRouteInfo>;

interface LocationState {
  bondInsuranceId?: string;
}

type SearchField = 'name' | 'document' | 'address' | 'property_code';

type ApiFilters = FilterData & { estateUnitId?: string };

const SEARCH_FIELDS: SearchField[] = ['name', 'document', 'address', 'property_code'];

export const canceledBondInsuranceAvailableStatus = [
  {
    key: 1,
    value: CanceledStatusEnum.CANCELLATION_IN_PROGRESS,
    text: 'Em análise'
  },
  {
    key: 2,
    value: CanceledStatusEnum.CANCELED,
    text: 'Concluído'
  },
  {
    key: 3,
    value: CanceledStatusEnum.PENDING,
    text: 'Pendente'
  },
  {
    key: 4,
    value: CanceledStatusEnum.UNCANCELLED,
    text: 'Não concluído'
  },
  {
    key: 5,
    value: CanceledStatusEnum.PENDING_BY_INSURER,
    text: 'Solicitado para Seguradora'
  },
  {
    key: 6,
    value: CanceledStatusEnum.FINANCIAL_PENDING,
    text: 'Pendente Financeiro'
  },
  {
    key: 7,
    value: CanceledStatusEnum.CLAIM_CANCELLATION,
    text: 'Cancelamento Sinistro'
  }
];

const statusSelectOptions = canceledBondInsuranceAvailableStatus.map(option => ({
  value: String(option.value),
  text: option.text
}));

const originSelectOptions = [
  { value: CanceledOrigin.ANALYZES, text: 'Análises' },
  { value: CanceledOrigin.PROPOSALS, text: 'Propostas' },
  { value: CanceledOrigin.POLICIES, text: 'Apólices' },
  { value: CanceledOrigin.EXTERNAL, text: 'Externos' }
];

const toSelectOptions = (
  items: Array<{ value: any; text: string }>
): Array<{ value: string; label: string }> =>
  items.map(item => ({
    value: String(item.value),
    label: String(item.text)
  }));

const formatDateValue = (value: any): string | undefined => {
  if (!value) return undefined;
  if (value instanceof Date) {
    return moment(value).format('YYYY-MM-DD');
  }
  const asString = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(asString)) {
    return asString.substring(0, 10);
  }
  const parsed = moment(asString, ['YYYY-MM-DD', 'DD/MM/YYYY'], true);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : asString;
};

const buildInitialFilterValues = (singleEstateId: string): FilterValues => ({
  type: 'name',
  argument: '',
  start: '',
  end: '',
  origin: '',
  estate_id: singleEstateId,
  insurer_id: '',
  status: '',
  estateUnitId: '',
  responsibleId: '',
  onlyUnread: false
});

const normalizeToApiFilter = (values: FilterValues): ApiFilters => {
  const searchType = values.type as SearchField | undefined;
  let argument = String(values.argument || '').trim();

  if (searchType === 'document') {
    argument = argument.replace(/\D/g, '');
  }

  const filter: ApiFilters = {
    isRv: true
  };

  SEARCH_FIELDS.forEach(field => {
    filter[field] = undefined;
  });

  if (searchType && argument && SEARCH_FIELDS.includes(searchType)) {
    filter[searchType] = argument;
  }

  if (values.estate_id) filter.estate_id = String(values.estate_id);
  if (values.insurer_id) filter.insurer_id = String(values.insurer_id);

  const start = formatDateValue(values.start);
  const end = formatDateValue(values.end);
  if (start) filter.start = start;
  if (end) filter.end = end;

  const status = String(values.status || '');
  if (status && status !== 'all' && status !== 'Todos') {
    filter.status = status;
  }

  const origin = String(values.origin || '');
  if (origin && origin !== CanceledOrigin.ALL) {
    filter.origin = origin;
  }

  if (values.responsibleId) {
    filter.responsibleId = String(values.responsibleId);
  }

  filter.onlyUnread = Boolean(values.onlyUnread);

  return appendEstateUnitIdToFilter(
    filter as Record<string, unknown>,
    String(values.estateUnitId || '')
  ) as ApiFilters;
};

const CanceledBondInsurances = ({
  availableBrokersAndEstates,
  user
}: BondInsuranceListProps) => {
  const location = useLocation();
  const locationState = location.state as LocationState | undefined;

  const brokers: CustoDropDownItemProps[] = availableBrokersAndEstates.brokers;
  const estates: CustoDropDownItemProps[] = availableBrokersAndEstates.estates;
  const insurers: CustoDropDownItemProps[] = availableBrokersAndEstates.bondInsurers;

  const isBackOffice = isBackOfficeUser();
  const isEstateUser = isEstateRole(user?.role);

  const singleEstateId = useMemo(() => {
    if (estates.length !== 1) return '';
    const value = estates[0]?.value;
    return String(typeof value === 'object' && value?.id ? value.id : value || '');
  }, [estates]);

  const singleBrokerId = useMemo(() => {
    if (brokers.length !== 1) return '';
    const value = brokers[0]?.value;
    return String(typeof value === 'object' && value?.id ? value.id : value || '');
  }, [brokers]);

  const initialFilterValues = useMemo(
    () => buildInitialFilterValues(singleEstateId),
    [singleEstateId]
  );

  const hasDeepLink = Boolean(locationState?.bondInsuranceId);

  const [canceledBondInsurances, setCanceledBondInsurances] = useState<
    GetCanceledBondInsurancesResponseDTO[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [dataCount, setDataCount] = useState(0);
  const [notRead, setNotRead] = useState<number[]>([]);
  const [bondInsuranceId, setBondInsuranceId] = useState<string | undefined>(
    locationState?.bondInsuranceId
  );
  const [appliedFilters, setAppliedFilters] = useState<ApiFilters>(() =>
    normalizeToApiFilter(initialFilterValues)
  );
  const [isEnabledToFetch, setIsEnabledToFetch] = useState(
    hasDeepLink || !isBackOffice
  );
  const [reportModalIsOpen, setReportModalIsOpen] = useState(false);
  const [reportState, setReportState] = useState<number | undefined>(undefined);
  const [responsibleOptions, setResponsibleOptions] = useState<
    Array<{ value: string; text: string }>
  >([]);
  const [isResponsibleLoading, setIsResponsibleLoading] = useState(false);

  const appliedFiltersRef = useRef(appliedFilters);
  const bondInsuranceIdRef = useRef(bondInsuranceId);

  useEffect(() => {
    appliedFiltersRef.current = appliedFilters;
  }, [appliedFilters]);

  useEffect(() => {
    bondInsuranceIdRef.current = bondInsuranceId;
  }, [bondInsuranceId]);

  const estateOptions = useMemo(
    () =>
      estates.map(item => {
        const valueId =
          typeof item.value === 'object' && item.value?.id
            ? item.value.id
            : item.value;
        return {
          value: String(valueId || ''),
          text: String(item.text || '')
        };
      }),
    [estates]
  );

  const insurerOptions = useMemo(
    () =>
      insurers.map(item => {
        const valueId =
          typeof item.value === 'object' && item.value?.id
            ? item.value.id
            : item.value;
        return {
          value: String(valueId || ''),
          text: String(item.text || '')
        };
      }),
    [insurers]
  );

  useEffect(() => {
    if (!user?.broker?.id || responsibleOptions.length > 0) return;

    setIsResponsibleLoading(true);
    getUsers(user.broker.id)
      .then(result => {
        const convertedValues: UserForUpdateEstateForm[] = result.map((item: any) => ({
          id: item.id,
          name: item.name
        }));

        setResponsibleOptions(
          UsersToCustomDropDownOptions(false, convertedValues).map((item: any) => ({
            value: String(item.value),
            text: String(item.text)
          }))
        );
      })
      .catch(() => {
        dangerNotification(
          'Oops...',
          'Não foi possível encontrar os analistas disponíveis'
        );
      })
      .finally(() => {
        setIsResponsibleLoading(false);
      });
  }, [user?.broker?.id, responsibleOptions.length]);

  const filterFields: FilterField[] = useMemo(() => {
    const fields: FilterField[] = [
      {
        kind: 'search',
        name: 'search',
        label: 'Pesquisar',
        placeholder: 'Digite...',
        defaultType: 'name',
        types: searchTypeOptions.map(option => ({
          value: String(option.value),
          label: String(option.text)
        })),
        width: 1.6
      },
      {
        kind: 'dateRange',
        name: 'period',
        label: 'Período',
        startName: 'start',
        endName: 'end'
      },
      {
        kind: 'select',
        name: 'origin',
        label: 'Origem',
        placeholder: 'Selecionar...',
        options: toSelectOptions(originSelectOptions),
        getDisplayValue: values => {
          const option = originSelectOptions.find(
            item => String(item.value) === String(values.origin)
          );
          return option ? option.text : undefined;
        }
      }
    ];

    if (!isEstateUser) {
      fields.push({
        kind: 'select',
        name: 'estate_id',
        label: 'Imobiliária',
        placeholder: 'Selecionar...',
        options: toSelectOptions(estateOptions),
        getDisplayValue: values => {
          const option = estateOptions.find(
            item => String(item.value) === String(values.estate_id)
          );
          return option ? option.text : undefined;
        }
      });
    }

    fields.push(
      {
        kind: 'select',
        name: 'insurer_id',
        label: 'Seguradora',
        placeholder: 'Selecionar...',
        options: toSelectOptions(insurerOptions),
        getDisplayValue: values => {
          const option = insurerOptions.find(
            item => String(item.value) === String(values.insurer_id)
          );
          return option ? option.text : undefined;
        }
      },
      {
        kind: 'select',
        name: 'status',
        label: 'Status',
        placeholder: 'Selecionar...',
        options: toSelectOptions(statusSelectOptions),
        getDisplayValue: values => {
          const option = statusSelectOptions.find(
            item => String(item.value) === String(values.status)
          );
          return option ? option.text : undefined;
        }
      },
      {
        kind: 'custom',
        name: 'estateUnitId',
        label: 'Unidade',
        advanced: true,
        getDisplayValue: values => {
          if (!values.estateUnitId) return undefined;
          if (values.estateUnitId === 'null') return 'Sem unidade';
          return String(values.estateUnitId);
        },
        render: ({ values, setValue }) => (
          <EstateUnitFilter
            estateId={String(values.estate_id || user?.estate?.id || '')}
            value={values.estateUnitId || ''}
            onChange={value => setValue('estateUnitId', value)}
            upward
          />
        )
      }
    );

    if (!isEstateUser) {
      fields.push({
        kind: 'select',
        name: 'responsibleId',
        label: 'Responsável',
        placeholder: 'Selecionar...',
        advanced: true,
        loading: isResponsibleLoading,
        options: toSelectOptions(responsibleOptions),
        getDisplayValue: values => {
          const option = responsibleOptions.find(
            item => String(item.value) === String(values.responsibleId)
          );
          return option ? option.text : undefined;
        }
      });
    }

    fields.push({
      kind: 'checkbox',
      name: 'onlyUnread',
      label: 'Mensagens não lidas',
      advanced: true,
      getDisplayValue: values => (values.onlyUnread ? 'Sim' : undefined)
    });

    return fields;
  }, [
    isEstateUser,
    estateOptions,
    insurerOptions,
    responsibleOptions,
    isResponsibleLoading,
    user?.estate?.id
  ]);

  const buildRequestFilter = useCallback((): ApiFilters | null => {
    const filter: ApiFilters = {
      ...appliedFiltersRef.current,
      isRv: true,
      user_id: user.id,
      ...(bondInsuranceIdRef.current
        ? { bondInsuranceId: bondInsuranceIdRef.current }
        : {})
    };

    if (!filter.estate_id && singleEstateId) {
      filter.estate_id = singleEstateId;
    }

    if (!filter.broker_id && singleBrokerId) {
      filter.broker_id = singleBrokerId;
    }

    if (!filter.broker_id && user?.broker?.id) {
      filter.broker_id = String(user.broker.id);
    }

    if (!filter.broker_id) {
      return null;
    }

    return filter;
  }, [singleBrokerId, singleEstateId, user?.broker?.id, user.id]);

  const getData = useCallback(async () => {
    setIsLoading(true);

    try {
      const filter = buildRequestFilter();
      if (!filter) {
        setCanceledBondInsurances([]);
        return;
      }

      const canceled = await getCanceledBondInsurances(currentPage, filter);

      if (currentPage === 1) setDataCount(canceled.count);
      setCanceledBondInsurances(canceled.data);

      if (bondInsuranceIdRef.current) {
        setBondInsuranceId(undefined);
      }
    } catch (e) {
      console.error({ e });
      dangerNotification('Oops...', 'Não foi possível buscar as análises');
    } finally {
      setIsLoading(false);
    }
  }, [buildRequestFilter, currentPage]);

  const handleExport = async () => {
    setIsLoading(true);
    try {
      const filter = buildRequestFilter();
      if (!filter) return;

      const status = await getCanceledBondInsurancesReport(filter);
      if (!status) {
        dangerNotification('Ops...', 'Erro ao solicitar relatório de sinistros.');
      } else {
        setReportState(status);
        setReportModalIsOpen(true);
      }
    } catch (e) {
      console.error({ e });
      dangerNotification('Oops...', 'Não foi possível exportar o relatório');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    window.history.replaceState(
      {
        ...(location.state as any),
        bondInsuranceId: undefined
      },
      ''
    );
  }, [location]);

  useEffect(() => {
    if (!isBackOffice) {
      setIsEnabledToFetch(true);
    }
  }, [isBackOffice]);

  useEffect(() => {
    if (!isEnabledToFetch) return;
    getData();
  }, [currentPage, appliedFilters, isEnabledToFetch, getData]);

  const handleFilterSearch = (values: FilterValues) => {
    const next = normalizeToApiFilter(values);
    if (!next.estate_id && singleEstateId) {
      next.estate_id = singleEstateId;
    }
    setBondInsuranceId(undefined);
    setAppliedFilters(next);
    setCurrentPage(1);
    setIsEnabledToFetch(true);
  };

  const handleFilterClear = () => {
    setBondInsuranceId(undefined);
    setCurrentPage(1);
    setIsEnabledToFetch(true);
  };

  const actualizeUnread = (index: number, value: number) => {
    const copyArray = [...notRead];
    copyArray[index] = value;
    setNotRead(copyArray);
  };

  return (
    <>
      {reportModalIsOpen && (
        <ReportModal
          isOpen={reportModalIsOpen}
          header="Relatório de Fianças Canceladas"
          message={
            reportState === 200
              ? `
              O relatório será gerado em background e será enviado o link para download via e-mail.
              Atenção, só será possível gerar um novo relatório após a finalização desse.
            `
              : 'Já existe um relatório na fila para ser gerado, aguarde a notificação via email.'
          }
          onClose={() => setReportModalIsOpen(false)}
        />
      )}

      <div style={{ marginBottom: 12 }}>
        <AuthorizationContainer
          action={ActionEnum.create}
          feature={FeatureEnum.canceleds}
        >
          <CreateCancelationModal />
        </AuthorizationContainer>
      </div>

      <FilterBar
        fields={filterFields}
        initialValues={initialFilterValues}
        onSearch={handleFilterSearch}
        onClear={handleFilterClear}
        loading={isLoading}
        syncToUrl
        urlNamespace="canceled"
        hasExternalActiveFilters={Boolean(bondInsuranceId)}
        actions={
          user.role && user.role.includes('BROKER') ? (
            <Button
              light
              type="button"
              content="Exportar"
              disabled={isLoading}
              onClick={handleExport}
              style={{ height: 38, minWidth: 110 }}
            />
          ) : null
        }
      />

      <CanceledBondInsurancesTable
        canceledBondInsurances={canceledBondInsurances}
        responsibleOptions={responsibleOptions.map((item, index) => ({
          key: index,
          value: item.value,
          text: item.text
        }))}
        notRead={notRead}
        setNotRead={actualizeUnread}
        isLoading={isLoading}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        getData={() => getData()}
        dataCount={dataCount}
      />
    </>
  );
};

export default CanceledBondInsurances;
